import React, { useState, useRef, useCallback } from "react";
import FlowNodeEditorShell from "../FlowBuilder/FlowNodeEditorShell";
import { useFlowNodeEdit } from "../../hooks/useFlowNodeEdit";
import { getNodeEditorMeta } from "../../utils/flowNodeEditorMeta";
import { Handle, Position } from "reactflow";
import {
  Webhook,
  Pencil,
  Check,
  Plus,
  Trash2,
  AlertTriangle,
  ArrowRightLeft,
  ArrowRight,
  Server,
  Variable,
} from "lucide-react";
import VariableDropdown from "../FlowBuilder/VariableDropdown";
import { getUnknownVariableNames } from "../../utils/flowVariableValidation";
import NodeActionButtons from "./NodeActionButtons";
import NodeEditorTabs from "./NodeEditorTabs";

// ─── Collapsed View ───────────────────────────────────
const CollapsedView = ({ data, onEdit, statusHandlers }) => {
  return (
    <div
      className="relative rounded-xl overflow-hidden"
      style={{ backgroundColor: "#FFF7ED", padding: "10px", minWidth: "260px", maxWidth: "320px" }}
    >
      <button
        onClick={(e) => { e.stopPropagation(); onEdit(); }}
        className="absolute top-2 right-2 z-10 p-1.5 bg-white/90 hover:bg-white rounded-full shadow-md transition-all hover:shadow-lg"
        title="Edit webhook"
      >
        <Pencil size={12} className="text-orange-600" />
      </button>

      <div className="bg-white rounded-lg shadow-sm p-3">
        <div className="flex items-center gap-2 mb-2">
          <div className="p-1.5 bg-orange-100 rounded-lg">
            <Webhook size={14} className="text-orange-600" />
          </div>
          <span className="text-[12px] font-bold text-orange-900">Webhook</span>
          {data.method && (
            <span className="text-[9px] font-bold bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded-full">
              {data.method}
            </span>
          )}
        </div>

        {data.webhookUrl && (
          <p className="text-[10px] text-gray-500 truncate mb-1.5">
            {data.webhookUrl.length > 40 ? data.webhookUrl.substring(0, 40) + "..." : data.webhookUrl}
          </p>
        )}

        {data.inputVariable && (
          <div className="text-[9px] text-orange-800 bg-orange-50 px-2 py-0.5 rounded mb-1">
            Input: <span className="font-mono font-semibold">{`{{${data.inputVariable}}}`}</span>
          </div>
        )}

        {/* Response mapping summary */}
        {(data.responseMapping || []).length > 0 && (
          <div className="flex items-center gap-1 text-[9px] text-blue-700 bg-blue-50 px-2 py-0.5 rounded mb-1">
            <ArrowRightLeft size={8} />
            <span className="font-medium">{data.responseMapping.length} response mapping(s)</span>
          </div>
        )}

        {/* Status handlers */}
        {statusHandlers.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1.5">
            {statusHandlers.map((sh) => (
              <div key={sh.id} className="relative">
                <span
                  className={`text-[9px] font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${
                    sh.statusCode >= 200 && sh.statusCode < 300
                      ? "bg-green-100 text-green-700"
                      : sh.statusCode >= 400
                      ? "bg-red-100 text-red-700"
                      : "bg-yellow-100 text-yellow-700"
                  }`}
                >
                  <Server size={8} />
                  {sh.statusCode}
                  {sh.connectedNodeId && (
                    <span className="w-1.5 h-1.5 rounded-full bg-current ml-0.5"></span>
                  )}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Expanded Editor ──────────────────────────────────
const ExpandedEditor = ({ data, onSave, onCancel, flowVariables, onCreateVariable }) => {
  const [webhookUrl, setWebhookUrl] = useState(data.webhookUrl || "");
  const [method, setMethod] = useState(data.method || "POST");
  const [headers, setHeaders] = useState(data.headers || "{}");
  const [requestBody, setRequestBody] = useState(data.requestBody || "");
  const [inputVariable, setInputVariable] = useState(data.inputVariable || "");
  const [responseVariable, setResponseVariable] = useState(data.responseVariable || "webhook_response");
  const [responseMessage, setResponseMessage] = useState(data.responseMessage || "");
  const [responseMapping, setResponseMapping] = useState(
    data.responseMapping || []
  );
  const [statusHandlers, setStatusHandlers] = useState(
    data.statusHandlers || []
  );
  const requestBodyRef = useRef(null);

  const insertVariableInBody = (varName) => {
    if (!varName) return;
    const textarea = requestBodyRef.current;
    const placeholder = `{{${varName}}}`;
    if (!textarea) {
      setRequestBody((prev) => prev + placeholder);
      return;
    }
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const before = requestBody.substring(0, start);
    const after = requestBody.substring(end);
    setRequestBody(before + placeholder + after);
    setTimeout(() => {
      const pos = start + placeholder.length;
      textarea.focus();
      textarea.setSelectionRange(pos, pos);
    }, 0);
  };

  // Validation warnings
  const bodyWarnings = getUnknownVariableNames(requestBody, flowVariables);
  const messageWarnings = getUnknownVariableNames(responseMessage, flowVariables);
  const statusMsgWarnings = statusHandlers.map((sh) => ({
    id: sh.id,
    unknown: getUnknownVariableNames(sh.messageTemplate || "", flowVariables),
  })).filter((w) => w.unknown.length > 0);

  const addResponseMapping = () => {
    setResponseMapping([
      ...responseMapping,
      { id: `rm_${Date.now()}`, responsePath: "", variableName: "" },
    ]);
  };

  const updateResponseMapping = (idx, field, value) => {
    const updated = [...responseMapping];
    updated[idx] = { ...updated[idx], [field]: value };
    setResponseMapping(updated);
  };

  const removeResponseMapping = (idx) => {
    setResponseMapping(responseMapping.filter((_, i) => i !== idx));
  };

  const addStatusHandler = () => {
    setStatusHandlers([
      ...statusHandlers,
      {
        id: `status_${Date.now()}`,
        statusCode: 200,
        messageTemplate: "",
        connectedNodeId: null,
      },
    ]);
  };

  const updateStatusHandler = (idx, field, value) => {
    const updated = [...statusHandlers];
    updated[idx] = { ...updated[idx], [field]: value };
    setStatusHandlers(updated);
  };

  const removeStatusHandler = (idx) => {
    setStatusHandlers(statusHandlers.filter((_, i) => i !== idx));
  };

  const handleSave = () => {
    onSave({
      webhookUrl,
      method,
      headers,
      requestBody,
      inputVariable,
      responseVariable,
      responseMessage,
      responseMapping,
      statusHandlers,
    });
  };

  return (
    <div
      className="nowheel"
      style={{ backgroundColor: "#FFF7ED", borderRadius: "12px", padding: "14px", minWidth: "380px", maxWidth: "440px" }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-orange-200 rounded-lg">
            <Webhook size={14} className="text-orange-700" />
          </div>
          <span className="text-[13px] font-bold text-orange-900">Webhook</span>
        </div>
        <div className="flex gap-1.5">
          <button
            onClick={handleSave}
            className="px-3 py-1.5 text-[11px] font-bold bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors flex items-center gap-1 shadow-sm"
          >
            <Check size={12} /> Save
          </button>
          <button
            onClick={onCancel}
            className="px-3 py-1.5 text-[11px] font-bold bg-white text-gray-600 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200"
          >
            Cancel
          </button>
        </div>
      </div>

      <NodeEditorTabs nodeType="webhook">
      <div className="bg-white rounded-lg shadow-sm p-3 space-y-3 max-h-[500px] overflow-y-auto">
        {/* URL + Method */}
        <div className="grid grid-cols-3 gap-2">
          <div className="col-span-2">
            <label className="text-[10px] font-bold text-gray-600 uppercase tracking-wider mb-1 block">URL *</label>
            <input
              type="url"
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
              placeholder="https://api.example.com/webhook"
              className="w-full px-2 py-1.5 text-[11px] bg-[#F0F0F0] rounded-lg border-0 focus:outline-none focus:ring-1 focus:ring-orange-400 placeholder:text-gray-400"
            />
          </div>
          <div>
            <label className="text-[10px] font-bold text-gray-600 uppercase tracking-wider mb-1 block">Method</label>
            <select
              value={method}
              onChange={(e) => setMethod(e.target.value)}
              className="w-full px-2 py-1.5 text-[11px] font-semibold bg-[#F0F0F0] rounded-lg border-0 focus:outline-none focus:ring-1 focus:ring-orange-400"
            >
              <option value="GET">GET</option>
              <option value="POST">POST</option>
              <option value="PUT">PUT</option>
              <option value="PATCH">PATCH</option>
            </select>
          </div>
        </div>

        {/* Input Variable */}
        <div>
          <label className="text-[10px] font-bold text-gray-600 uppercase tracking-wider mb-1 block">Input Variable</label>
          <VariableDropdown
            value={inputVariable}
            onChange={(name) => setInputVariable(name)}
            flowVariables={flowVariables}
            allowCreate
            onCreateVariable={onCreateVariable}
            placeholder="Select input variable..."
          />
        </div>

        {/* Headers */}
        <div>
          <label className="text-[10px] font-bold text-gray-600 uppercase tracking-wider mb-1 block">Headers (JSON)</label>
          <textarea
            value={headers}
            onChange={(e) => setHeaders(e.target.value)}
            rows={2}
            className="w-full px-2 py-1.5 text-[10px] font-mono bg-[#F0F0F0] rounded-lg border-0 focus:outline-none focus:ring-1 focus:ring-orange-400 resize-none placeholder:text-gray-400"
            placeholder='{"Authorization": "Bearer token"}'
          />
        </div>

        {/* Request Body */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-[10px] font-bold text-gray-600 uppercase tracking-wider">Request Body (JSON)</label>
            <div className="flex items-center gap-1">
              <Variable size={10} className="text-orange-600" />
              <VariableDropdown
                value=""
                onChange={(name) => { if (name) insertVariableInBody(name); }}
                flowVariables={flowVariables}
                allowCreate
                onCreateVariable={onCreateVariable}
                placeholder="Insert variable..."
                className="min-w-[120px]"
              />
            </div>
          </div>
          <textarea
            ref={requestBodyRef}
            value={requestBody}
            onChange={(e) => setRequestBody(e.target.value)}
            rows={3}
            className="w-full px-2 py-1.5 text-[10px] font-mono bg-[#F0F0F0] rounded-lg border-0 focus:outline-none focus:ring-1 focus:ring-orange-400 resize-none placeholder:text-gray-400"
            placeholder='{"phone_number": "{{phone_number}}", "last_user_message": "{{last_user_message}}"}'
          />
          {bodyWarnings.length > 0 && (
            <div className="flex items-start gap-1 mt-1 px-2 py-1 bg-amber-50 rounded text-[9px] text-amber-700">
              <AlertTriangle size={10} className="flex-shrink-0 mt-0.5" />
              <span>Unknown variable(s): <strong>{bodyWarnings.join(", ")}</strong>. Add them in Variables.</span>
            </div>
          )}
        </div>

        {/* Response Variable */}
        <div>
          <label className="text-[10px] font-bold text-gray-600 uppercase tracking-wider mb-1 block">Response Variable</label>
          <VariableDropdown
            value={responseVariable}
            onChange={(name) => setResponseVariable(name)}
            flowVariables={flowVariables}
            allowCreate
            onCreateVariable={onCreateVariable}
            placeholder="Select response variable..."
          />
        </div>

        {/* Response Message */}
        <div>
          <label className="text-[10px] font-bold text-gray-600 uppercase tracking-wider mb-1 block">Success Message</label>
          <textarea
            value={responseMessage}
            onChange={(e) => setResponseMessage(e.target.value)}
            rows={2}
            className="w-full px-2 py-1.5 text-[11px] bg-[#F0F0F0] rounded-lg border-0 focus:outline-none focus:ring-1 focus:ring-orange-400 resize-none placeholder:text-gray-400"
            placeholder="Order {{order_id}} status: {{status}}"
          />
          {messageWarnings.length > 0 && (
            <div className="flex items-start gap-1 mt-1 px-2 py-1 bg-amber-50 rounded text-[9px] text-amber-700">
              <AlertTriangle size={10} className="flex-shrink-0 mt-0.5" />
              <span>Unknown variable(s): <strong>{messageWarnings.join(", ")}</strong></span>
            </div>
          )}
        </div>

        {/* ── Response Mapping ─────────────────────── */}
        <div className="border-t border-gray-100 pt-2">
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-[10px] font-bold text-gray-600 uppercase tracking-wider flex items-center gap-1">
              <ArrowRightLeft size={10} /> Response Mapping
            </label>
            <button
              onClick={addResponseMapping}
              className="text-[9px] font-bold text-orange-600 hover:text-orange-700 flex items-center gap-0.5"
            >
              <Plus size={9} /> Add
            </button>
          </div>
          {responseMapping.length === 0 && (
            <p className="text-[9px] text-gray-400 italic">No mappings. Click "Add" to map response fields to variables.</p>
          )}
          {responseMapping.map((rm, idx) => (
            <div key={rm.id} className="flex items-start gap-1.5 mb-2 bg-gray-50 rounded-lg p-2">
              <div className="flex-1 space-y-1">
                <input
                  type="text"
                  value={rm.responsePath}
                  onChange={(e) => updateResponseMapping(idx, "responsePath", e.target.value)}
                  placeholder="data (full body) or .data or data.status"
                  className="w-full px-2 py-1 text-[10px] font-mono bg-white rounded border border-gray-200 focus:outline-none focus:ring-1 focus:ring-orange-300 placeholder:text-gray-400"
                />
                <VariableDropdown
                  value={rm.variableName}
                  onChange={(name) => updateResponseMapping(idx, "variableName", name)}
                  flowVariables={flowVariables}
                  allowCreate
                  onCreateVariable={onCreateVariable}
                  placeholder="Store in variable..."
                />
              </div>
              <button
                onClick={() => removeResponseMapping(idx)}
                className="p-1 text-red-400 hover:text-red-600 mt-1"
              >
                <Trash2 size={10} />
              </button>
            </div>
          ))}
        </div>

        {/* ── Status Code Handlers ────────────────── */}
        <div className="border-t border-gray-100 pt-2">
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-[10px] font-bold text-gray-600 uppercase tracking-wider flex items-center gap-1">
              <Server size={10} /> Status Handlers
            </label>
            <button
              onClick={addStatusHandler}
              className="text-[9px] font-bold text-orange-600 hover:text-orange-700 flex items-center gap-0.5"
            >
              <Plus size={9} /> Add
            </button>
          </div>
          {statusHandlers.length === 0 && (
            <p className="text-[9px] text-gray-400 italic">No handlers. Add status codes to branch by response status.</p>
          )}
          {statusHandlers.map((sh, idx) => {
            const shWarning = statusMsgWarnings.find((w) => w.id === sh.id);
            return (
              <div key={sh.id} className="bg-gray-50 rounded-lg p-2 mb-2">
                <div className="flex items-center gap-1.5 mb-1">
                  <input
                    type="number"
                    value={sh.statusCode}
                    onChange={(e) => updateStatusHandler(idx, "statusCode", parseInt(e.target.value) || 0)}
                    className="w-16 px-2 py-1 text-[10px] font-bold bg-white rounded border border-gray-200 focus:outline-none focus:ring-1 focus:ring-orange-300"
                    placeholder="200"
                  />
                  <span className="text-[9px] text-gray-500 flex-1">Status code</span>
                  <div className={`w-2.5 h-2.5 rounded-full ${sh.connectedNodeId ? "bg-green-500" : "bg-gray-300"}`}
                    title={sh.connectedNodeId ? "Connected" : "Not connected"}
                  />
                  <button
                    onClick={() => removeStatusHandler(idx)}
                    className="p-0.5 text-red-400 hover:text-red-600"
                  >
                    <Trash2 size={10} />
                  </button>
                </div>
                <textarea
                  value={sh.messageTemplate || ""}
                  onChange={(e) => updateStatusHandler(idx, "messageTemplate", e.target.value)}
                  rows={2}
                  className="w-full px-2 py-1 text-[10px] bg-white rounded border border-gray-200 focus:outline-none focus:ring-1 focus:ring-orange-300 resize-none placeholder:text-gray-400"
                  placeholder="Message for this status (optional, supports {{var}})"
                />
                {shWarning && (
                  <div className="flex items-start gap-1 mt-1 px-2 py-0.5 bg-amber-50 rounded text-[9px] text-amber-700">
                    <AlertTriangle size={8} className="flex-shrink-0 mt-0.5" />
                    <span>Unknown: <strong>{shWarning.unknown.join(", ")}</strong></span>
                  </div>
                )}
                {/* Connection indicator */}
                {sh.connectedNodeId && (
                  <div className="flex items-center gap-1.5 mt-1.5 px-2 py-1 bg-orange-50 rounded-lg">
                    <ArrowRight size={10} className="text-orange-600" />
                    <span className="text-[9px] text-orange-600 font-semibold">
                      Connected to node
                    </span>
                    <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      </NodeEditorTabs>

      {/* Bottom buttons */}
      <div className="flex gap-2 mt-3">
        <button
          onClick={handleSave}
          className="flex-1 py-2 text-[12px] font-bold bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors flex items-center justify-center gap-1.5 shadow-sm"
        >
          <Check size={14} /> Save
        </button>
        <button
          onClick={onCancel}
          className="flex-1 py-2 text-[12px] font-bold bg-white text-gray-600 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

// ─── Main WebhookNode ─────────────────────────────────
const WebhookNode = ({ id, data }) => {
  const { isEditing, openEditor, closeEditor } = useFlowNodeEdit(id, data);
  const editorMeta = getNodeEditorMeta("webhook");

  const statusHandlers = data.statusHandlers || [];

  const handleSave = useCallback(
    (formData) => {
      if (data.onNodeDataChange) {
        data.onNodeDataChange(id, { ...data, ...formData });
      }
      closeEditor();
    },
    [id, data, closeEditor]);

  return (
    <div className={`relative transition-all shadow-md`} style={{ borderRadius: "12px" }}>
      <NodeActionButtons id={id} data={data} />
      <Handle
        type="target"
        position={Position.Left}
        className="!w-3.5 !h-3.5 !bg-orange-500 !border-2 !border-white !shadow-sm"
        style={{ left: -7 }}
      />

      <CollapsedView data={data} onEdit={openEditor} statusHandlers={statusHandlers} />
      <FlowNodeEditorShell
        open={isEditing}
        onClose={closeEditor}
        title={data.label || editorMeta.label}
        subtitle={editorMeta.label}
        icon={editorMeta.icon}
        accent={editorMeta.accent}
      >
        <ExpandedEditor
          data={data}
          onSave={handleSave}
          onCancel={() => closeEditor()}
          flowVariables={data.flowVariables || []}
          onCreateVariable={data.onCreateVariable}
         presentation="fullscreen" onCancel={closeEditor} />
      </FlowNodeEditorShell>

      {/* Dynamic source handles for status handlers */}
      {statusHandlers.length > 0 ? (
        statusHandlers.map((sh, index) => (
          <Handle
            key={sh.id}
            type="source"
            position={Position.Right}
            id={`status_${sh.id}`}
            className="!w-3 !h-3 !border-2 !border-white !shadow-sm"
            style={{
              right: -6,
              top: `${((index + 1) / (statusHandlers.length + 1)) * 100}%`,
              backgroundColor: sh.connectedNodeId
                ? "#22c55e"
                : sh.statusCode >= 200 && sh.statusCode < 300
                ? "#22c55e"
                : sh.statusCode >= 400
                ? "#ef4444"
                : "#f59e0b",
            }}
            title={`Status ${sh.statusCode}`}
          />
        ))
      ) : (
        <Handle
          type="source"
          position={Position.Right}
          className="!w-3.5 !h-3.5 !bg-orange-500 !border-2 !border-white !shadow-sm"
          style={{ right: -7 }}
        />
      )}

      {/* Status handler labels under node for connection handles */}
      {statusHandlers.length > 0 && (
        <div className="absolute -right-16 top-0 h-full flex flex-col justify-around items-start py-4 pointer-events-none">
          {statusHandlers.map((sh) => (
            <span
              key={sh.id}
              className="text-[7px] text-orange-700 font-semibold truncate max-w-[50px] text-center"
              title={`Status ${sh.statusCode}`}
            >
              {sh.statusCode}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default WebhookNode;
